import { AxiosResponse } from "axios";
import { AliasBadgeViewModel, BadgeViewModel } from "../badges/BadgeStore";
import axios, { APIWrapperResponse, DefaultResponse, TypedResponse } from "../general/HttpClient";
import { MatchStatViewModel } from "../matches/MatchesStore";
import { StatViewModel } from "../stats/StatsStore";
import { TeamMembershipViewModel, TeamViewModel } from "../teams/TeamsStore";

export function getAlias(id) {
    return axios.get(`/api/Aliases/getAlias/${id}`).then((response: AxiosResponse<APIWrapperResponse<AliasViewModel>>) => {
        return response.data.result;
    });
}

export function getAliasesForUser(id, includeArchive = false) {
    return axios.get(`/api/Aliases/getAliasesForUser/${id}`, { params: { includeArchive } }).then((response: AxiosResponse<APIWrapperResponse<AliasViewModel[]>>) => {
        return response.data.result;
    });
}

export function searchUsersExt(excludeExisting, query = "", organizationID = "", skip = 0, limit = 50, allowStaff = false, allowStudents = false, allowParents = false, allowGuests = false) {
    return axios
        .get("/api/Aliases/searchUsers", { params: { excludeExisting, organizationID, query, skip, limit, allowStaff, allowStudents, allowParents, allowGuests } })
        .then((response: AxiosResponse<APIWrapperResponse<GetIdentityOrganizationMembersResponse>>) => {
            return response.status == 200 ? response.data.result : new GetIdentityOrganizationMembersResponse();
        });
}

export function searchUsers(excludeExistingAliases, query = "", organizationID = "", skip = 0, limit = 200) {
    return Promise.all([
        searchUsersExt(excludeExistingAliases, query, organizationID, skip, limit, true, false, false, false),
        searchUsersExt(excludeExistingAliases, query, organizationID, skip, limit, false, true, false, false),
        searchUsersExt(excludeExistingAliases, query, organizationID, skip, limit, false, false, false, true),
    ]);
}

export function searchAliases(query = "", organizationID = "", skip = 0, limit = 200, includeArchive = false) {
    return axios.get("/api/Aliases/searchAliases", { params: { query, organizationID, skip, limit, includeArchive } }).then((response: AxiosResponse<APIWrapperResponse<Array<AliasViewModel>>>) => {
        return response.data.result;
    });
}

export function createIdentityAlias(request: CreateIdentityAliasRequest) {
    return axios.post(`/api/Aliases/createIdentityAlias`, request).then((response: AxiosResponse<APIWrapperResponse<CreateAliasResponse>>) => {
        return response.data.result;
    });
}

export function updateIdentityAlias(request: UpdateIdentityAliasRequest) {
    return axios.put(`/api/Aliases/updateIdentityAlias`, request).then((response: AxiosResponse<APIWrapperResponse<DefaultResponse>>) => {
        return response.data.result;
    });
}

export function createAlias(request: CreateAliasRequest) {
    return axios.post(`/api/Aliases/createAlias`, request).then((response: AxiosResponse<APIWrapperResponse<CreateAliasResponse>>) => {
        return response.data.result;
    });
}

export function createOwnAlias(request: CreateAliasRequest) {
    return axios.post(`/api/Aliases/createOwnAlias`, request).then((response: AxiosResponse<APIWrapperResponse<CreateAliasResponse>>) => {
        return response.data.result;
    });
}

export function updateAlias(request: UpdateAliasRequest | AliasViewModel) {
    return axios.put(`/api/Aliases/updateAlias`, request).then((response: AxiosResponse<APIWrapperResponse<DefaultResponse>>) => {
        return response.data.result;
    });
}

export function createAliasAndUser(request: CreateAliasRequest) {
    return axios.post(`/api/Aliases/createAliasAndUser`, request).then((response: AxiosResponse<APIWrapperResponse<CreateAliasResponse>>) => {
        return response.data.result;
    });
}

export function deleteAlias(id: string) {
    return axios.delete(`/api/Aliases/deleteAlias/${id}`).then((response: AxiosResponse<APIWrapperResponse<DefaultResponse>>) => {
        return response.data.result;
    });
}

export function getAliasOverview(id: string) {
    return axios.get(`/api/Aliases/getAliasOverview/${id}`).then((response: AxiosResponse<APIWrapperResponse<TypedResponse<AliasOverview>>>) => {
        return response.data.result;
    });
}

export function removeAliasProfileBackground(id: string) {
    return axios.put(`/api/Aliases/removeProfileBackground`, { id }).then((response: AxiosResponse<APIWrapperResponse<CreateAliasResponse>>) => {
        return response.data.result;
    });
}

export class AliasViewModel {
    id: string;
    name: string;
    identityUserID?: string;
    email: string;
    photoUri: string;
    splashUri: string;
    profileUri: string;
    rating: number;
    teams?: TeamMembershipViewModel[];
    identityUser?: IdentityUser;
    discordID?: string;
    badges: AliasBadgeViewModel[];

    archived: boolean;

    public static displayName(alias: AliasViewModel) {
        if (alias.identityUser) {
            if (alias.identityUser.displayName == alias.name) {
                return alias.name;
            } else {
                return alias.name + " - " + alias.identityUser.displayName;
            }
        }
        return alias.name;
    }
}
export interface CreateAliasResponse {
    success: boolean;
    aliasID: string;
    errors: string[];
}

export interface CreateIdentityAliasRequest {
    userID: string;
    organizationID: string;
}

export interface CreateAliasRequest {
    name: string;
    identityUserID: string;
    organizationID: string;
    email: string;
    photoUri: string;
    photoContent: string;
    splashUri: string;
    splashContent: string;
    profileUri: string;
    profileContent: string;
    archived: boolean;
}

export class GetIdentityOrganizationMembersResponse {
    constructor() {
        this.members = [];
        this.total = 0;
    }

    members: IdentityUser[];
    total: number;
}

export interface IdentityUser {
    id: string;
    userName: string;
    email: string;
    firstName: string;
    lastName: string;
    fullName: string;
    displayName: string;
    hasGivenAccessTo: DelegatedAccess[];
    groups: IdentityGroup[];
    sourceID: string;
    source: string;
    organization: string;
    membershipType: number;
    photo: string;
}

export interface DelegatedAccess {
    delegatorID: string;
    delegateeID: string;
    delegationRules: string;
}

export interface SourceIdentifier {
    source: string;
    sourceID: string;
}

export interface IdentityGroup {
    id: string;
    name: string;
    type: number;
    source: string;
    sourceID: string;
    organizationID: string;
    groupCustomTypeID: string;
    groupCustomTypeName: string;
    groupCustomTypeSource: string;
    groupCustomTypeSourceID: string;
    description: string;
}

export interface UpdateAliasRequest {
    id: string;
    identityUserID: string;
    name: string;
    email: string;
    photoUri: string;
    photoContent: string;
    splashUri: string;
    splashContent: string;
    profileUri: string;
    profileContent: string;

    archived: boolean;
}

export interface UpdateIdentityAliasRequest {
    userID: string;
    aliasID: string;
}

export interface AliasOverview {
    matchesPlayed: number;
    matchesWon: number;
    teams: TeamViewModel[];
    stats: MatchStatViewModel[];
    matchDays: string[];
}
